import { Textarea } from "../Form/Textarea";
import { Button } from "../Buttons/Button";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { IoClose } from "react-icons/io5";
import clsx from "clsx";
import { useUpdateUserRating } from "../../api/hooks";
import { Controller } from "react-hook-form";

export default function ReviewCard({ shouldShowReviewPrompt = true }) {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
    reset,
    register,
  } = useForm({
    defaultValues: {
      stars: 0,
      message: "",
    },
  });

  const stars = watch("stars");
  const [hoveredStar, setHoveredStar] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [visible, setVisible] = useState(false);
  const [previousState, setPreviousState] = useState(null);

  useEffect(() => {
    if (shouldShowReviewPrompt) {
      setTimeout(() => setVisible(true), 50);
    }
  }, [shouldShowReviewPrompt]);

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => {
      setHoveredStar(0);
      setShowForm(false);
      setShowOptions(false);
      reset();
    }, 500);
  };

  const { mutate: updateUserRating } = useUpdateUserRating(dismiss);

  const handleClose = () => {
    if (stars > 0) {
      setPreviousState({ stars, showForm: true });
    } else {
      setPreviousState({ stars: 0, showForm: false });
    }

    setShowForm(false);
    setShowOptions(true);
  };

  const handleMaybeLater = () => {
    updateUserRating({ action: "defer" });
  };

  const handleDecline = () => {
    updateUserRating({ action: "reject" });
  };

  const handleGoBack = () => {
    if (previousState) {
      setValue("stars", previousState.stars);
      setShowForm(previousState.showForm);
    } else {
      setValue("stars", 0);
      setShowForm(false);
    }
    setShowOptions(false);
  };

  const onSubmit = (data) => {
    const payload = {
      action: "submit",
      stars: data.stars,
      message: data.message || null,
    };

    updateUserRating(payload);
  };

  if (!shouldShowReviewPrompt) return null;

  return (
    <div
      className={clsx(
        "fixed right-8 bottom-8 z-50 max-w-[270px] w-full bg-white rounded-2xl p-5 shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all duration-500 ease-in-out",
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-12 opacity-0 pointer-events-none"
      )}
    >
      {!showOptions ? (
        <>
          <div className="flex items-center justify-between">
            <h1 className="text-base font-medium">How was your experience?</h1>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <IoClose className="cursor-pointer h-5 w-5" />
            </button>
          </div>

          <Controller
            control={control}
            name="stars"
            rules={{ required: "Please select a star rating" }}
            render={({ field }) => (
              <div
                className={clsx(
                  "flex space-x-3 mt-4 items-center w-full rounded-md px-1 py-2 transition-shadow duration-300",
                  errors.stars && "ring-2 ring-red-400"
                )}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      field.onChange(value); // set RHF value
                      setShowForm(true); // show form after rating
                    }}
                    onMouseEnter={() => setHoveredStar(value)}
                    onMouseLeave={() => setHoveredStar(0)}
                    className={clsx(
                      "text-2xl transition-colors duration-200",
                      (hoveredStar || field.value) >= value
                        ? "text-[rgb(59,130,246)]"
                        : "text-gray-300"
                    )}
                  >
                    ★
                  </button>
                ))}
              </div>
            )}
          />
          {errors.stars && (
            <p className="text-sm text-red-500 mt-1">{errors.stars.message}</p>
          )}

          {showForm && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="transition-opacity duration-300 mt-4"
            >
              <input type="hidden" {...register("stars", { required: true })} />

              <Textarea
                control={control}
                rows={6}
                placeholder="Tell us more (optional)"
                name="message"
              />

              <div className="mt-6">
                <Button
                  type="submit"
                  shape="oval"
                  text="Submit"
                  className="w-full"
                />
              </div>
            </form>
          )}
        </>
      ) : (
        <>
          <h2 className="text-base font-medium mb-5">
            Would you like to leave feedback later?
          </h2>

          <div className="space-y-4">
            <Button
              shape="oval"
              text="Maybe Later"
              onClick={handleMaybeLater}
              className="w-full"
            />
            <Button
              shape="oval"
              text="No Thanks"
              onClick={handleDecline}
              className="w-full bg-gray-100 text-gray-800 hover:bg-gray-200"
            />
            <div className="flex justify-end">
              <button
                onClick={handleGoBack}
                className="mr-2 flex items-center text-sm text-gray-500 hover:text-gray-700 hover:underline"
              >
                Go Back
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
